import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import images from "../../images";
import { OTPInput } from "../../components/OtpInputProps/OTPInputProps";
import { toast } from "react-toastify";
import {
  useVerifyOTPMutation,
  useResendOTPMutation,
} from "../../services/apiSlice";
import Countdown from "../../components/countdown/countdown";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Copyright from "../../layout/Copyright/Copyright";

export default function EmailOTPPage() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get("business_id");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifyOTP, { isLoading: isVerifying }] = useVerifyOTPMutation();
  const [resendOTP, { isLoading: isResending }] = useResendOTPMutation();

  const handleVerifyOTP = async () => {
    if (isVerifying) return;

    if (otp.some((digit) => digit === "")) {
      toast.error(t("verifyOTP.pleaseEnterValidCompleteOTP"));
      return;
    }

    if (!businessId) {
      toast.error(t("verifyOTP.pleaseSigUpAgain"));
      return;
    }

    const body = {
      business_id: businessId,
      otp_code: otp.join(""),
    };

    try {
      const response = await verifyOTP(body).unwrap();
      if (
        response.message === "Verify OTP success" ||
        response.status === 200
      ) {
        sessionStorage.setItem("accessToken", response.data?.access_token);
        sessionStorage.setItem("refreshToken", response.data.refresh_token);
        toast.success(t("verifyOTP.verifySuccess"));
        navigate("/search-company");
      }
    } catch (err) {
      if (err.name === "TypeError" && err.message.includes("NetworkError")) {
        toast.error(t("verifyOTP.networkError"));
      } else {
        toast.error(err?.data?.detail[0].msg || t("verifyOTP.verifyFailed"));
      }
    }
  };

  const handleResendOTP = async () => {
    if (isResending || resendCooldown > 0) {
      if (resendCooldown > 0) {
        toast.info(
          t("base.pleaseWait")`${resendCooldown}` +
            t("verifyOTP.secondBeforeResend")
        );
      }
      return;
    }

    if (!businessId) {
      toast.error(t("verifyOTP.pleaseSigUpAgain"));
      return;
    }

    const body = {
      business_id: businessId.trim(),
    };

    try {
      const response = await resendOTP(body).unwrap();
      if (
        response.message === "Operation successful" ||
        response.status === 200
      ) {
        toast.success(t("verifyOTP.resendOTPSuccess"));
        setResendCooldown(30);
      }
    } catch (err) {
      if (err?.data?.message) {
        toast.error(`Error: ${err.data.message}`);
      } else if (
        err.name === "TypeError" &&
        err.message.includes("NetworkError")
      ) {
        toast.error(t("verifyOTP.networkError"));
        // toast.success("Resend OTP successfully");
        setResendCooldown(30);
      } else {
        toast.error(t("verifyOTP.resendOTPFailed"));
      }
    }
  };
  const handleCountdownComplete = () => {
    setResendCooldown(0);
  };

  return (
    <main className="flex overflow-hidden flex-col pt-14 mx-auto w-full bg-yellow-50 max-w-[480px]">
      <Helmet>
        <meta name="theme-color" content="#fefce8" />
      </Helmet>
      <header className="flex justify-center items-center w-full ">
        <img src={images.LogoLogin} className="w-[198px]" alt="Logo" />
      </header>
      <section className="flex flex-col justify-between items-center px-11 py-9 mt-24 w-full bg-white min-h-[666px] rounded-[30px_30px_0px_0px] shadow-[0px_0px_4px_rgba(0,0,0,0.08)]">
        <div className="flex flex-col items-center justify-center max-w-full w-[340px]">
          <header className="self-center w-full text-center">
            <div className="flex flex-col items-center w-full">
              <h1 className="text-4xl font-bold text-gray-800">
                {t("verifyOTP.title")}
              </h1>
              <span className="gap-2.5 self-stretch px-3 py-1.5 mt-2.5 text-xs leading-none text-amber-400 bg-yellow-50 rounded-xl">
                {t("verifyOTP.enterOTP")}
              </span>
            </div>
          </header>

          <div className="flex flex-col mt-7 w-full items-center text-sm leading-none text-neutral-500">
            <div className="mt-4">
              <OTPInput otp={otp} setOtp={setOtp} disabled={isVerifying} />
            </div>
            <button
              onClick={handleVerifyOTP}
              className={`overflow-hidden gap-2.5 self-stretch px-4 py-5 mt-6 w-full text-lg font-semibold leading-none text-white bg-[#3F54DF] rounded-xl min-h-[54px] ${
                isVerifying
                  ? "opacity-70 cursor-not-allowed"
                  : "hover:bg-indigo-700"
              }`}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <div className="flex items-center justify-center">
                  {t("base.verify")}
                </div>
              ) : (
                <>{t("base.continue")}</>
              )}
            </button>

            <p className="self-start mt-6 w-full font-medium text-center">
              <span
                style={{
                  fontFamily:
                    "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: 400,
                  color: "rgba(120,122,122,1)",
                }}
              >
                {t("verifyOTP.noRecieveOTP")}
              </span>{" "}
              <span
                onClick={handleResendOTP}
                style={{
                  fontFamily:
                    "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: 700,
                  color: "rgba(63,84,223,1)",
                  cursor:
                    isResending || resendCooldown > 0
                      ? "not-allowed"
                      : "pointer",
                  opacity: isResending || resendCooldown > 0 ? 0.6 : 1,
                }}
              >
                {isResending ? (
                  <>{t("base.sending")}</>
                ) : resendCooldown > 0 ? (
                  <>
                    {t("verifyOTP.resendOTP")} (
                    <Countdown
                      seconds={resendCooldown}
                      onComplete={handleCountdownComplete}
                    />
                    )
                  </>
                ) : (
                  <> {t("verifyOTP.resendOTP")}</>
                )}
              </span>
            </p>
          </div>
          <div className="mt-5 w-full text-center">
            <span
              onClick={(e) => {
                navigate("/register");
              }}
              className="text-indigo-600 font-bold cursor-pointer"
            >
              {t("companyProfile.backToSignUp")}
            </span>
          </div>
        </div>
        <div className="flex justify-center">
          <Copyright />
        </div>
      </section>
    </main>
  );
}
